import React from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Paper,
    Container,
} from "@mui/material";
import {
    Refresh as RefreshIcon,
    BugReport as BugIcon,
} from "@mui/icons-material";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an error:", error, errorInfo);
        this.setState({
            error: error,
            errorInfo: errorInfo,
        });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Paper sx={{ p: 4, textAlign: "center" }}>
                        <Box sx={{ mb: 3 }}>
                            <BugIcon
                                sx={{
                                    fontSize: 64,
                                    color: "error.main",
                                    mb: 2,
                                }}
                            />
                            <Typography variant="h4" gutterBottom>
                                Something went wrong
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ mb: 3 }}
                            >
                                We encountered an error while rendering the
                                Funnel builder. This might be due to a temporary
                                issue or invalid data.
                            </Typography>
                        </Box>

                        <Alert
                            severity="error"
                            sx={{ mb: 3, textAlign: "left" }}
                        >
                            <Typography variant="subtitle2" gutterBottom>
                                Error Details:
                            </Typography>
                            <Typography
                                variant="body2"
                                component="pre"
                                sx={{
                                    fontSize: "0.75rem",
                                    overflow: "auto",
                                    maxHeight: "200px",
                                }}
                            >
                                {this.state.error &&
                                    this.state.error.toString()}
                            </Typography>
                        </Alert>

                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={this.handleRetry}
                            >
                                Try Again
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => window.location.reload()}
                            >
                                Reload Page
                            </Button>
                        </Box>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 3, display: "block" }}
                        >
                            If this problem persists, please contact support
                            with the error details above.
                        </Typography>
                    </Paper>
                </Container>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
